import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronRight, ArrowLeft, MapPin, Clock, Route as RouteIcon, Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ROUTES_DATA } from "@/lib/routes";
import { PHONE_DISPLAY, telLink, whatsappLink, buildBookingMessage } from "@/lib/whatsapp";
import { createBooking } from "@/lib/api";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import LenisScroll from "@/components/site/LenisScroll";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingActions from "@/components/site/FloatingActions";

const VEHICLES = [
  { name: "Swift Dzire", seats: 4, tag: "Sedan" },
  { name: "Toyota Etios", seats: 4, tag: "Sedan" },
  { name: "Maruti Ertiga", seats: 6, tag: "MPV" },
  { name: "Toyota Innova", seats: 7, tag: "SUV" },
  { name: "Innova Crysta", seats: 7, tag: "Premium SUV" },
  { name: "Tempo Traveller", seats: 12, tag: "Group" },
];

export default function RouteLanding() {
  const { slug } = useParams();
  const route = ROUTES_DATA[slug];

  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 120]);
  const scaleImg = useTransform(scrollY, [0, 800], [1, 1.08]);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    pickup_location: "",
    drop_location: "",
    pickup_date: "",
    pickup_time: "",
    vehicle_type: "Swift Dzire",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!route) return;
    document.title = route.seoTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", route.seoDescription);
    setForm((f) => ({ ...f, pickup_location: route.from, drop_location: route.to }));
    // Scroll to top on page mount
    window.scrollTo(0, 0);
    return () => { document.title = "Mamta Tours & Travels | Best Taxi & Cab Service in Jodhpur, Rajasthan"; };
  }, [route, slug]);

  if (!route) return <Navigate to="/" replace />;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error("Please add your name & mobile.");
      return;
    }
    setLoading(true);
    try { await createBooking(form); } catch (err) { console.error(err); }
    setLoading(false);
    const msg = buildBookingMessage(form);
    toast.success("Opening WhatsApp with your booking details...");
    window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
  };

  // FAQ JSON-LD schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": route.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mamtatoursandtravels.com/" },
      { "@type": "ListItem", position: 2, name: `Taxi from ${route.from} to ${route.to}`, item: `https://mamtatoursandtravels.com/taxi/${route.slug}` },
    ],
  };

  return (
    <div className="relative bg-brand-cream" data-testid={`route-page-${route.slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />

      <LenisScroll />
      <Header />

      {/* HERO */}
      <section className="relative min-h-[95svh] w-full overflow-hidden bg-brand-blue text-white">
        <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 -z-0">
          <img src={route.heroImage} alt={`${route.from} to ${route.to}`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-brand-blue/70" />
        </motion.div>
        <div className="grain absolute inset-0 pointer-events-none z-[1]" aria-hidden />

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-16">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 text-xs tracking-[0.24em] uppercase text-white/70 flex items-center gap-2">
            <Link to="/" className="hover:text-brand-orange inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Home</Link>
            <span>/</span>
            <span className="text-white">Taxi · {route.from} → {route.to}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-1.5 text-xs uppercase tracking-[0.22em] font-semibold">
                <RouteIcon className="w-3.5 h-3.5 text-brand-orange" />
                {route.distance} · {route.duration}
              </div>
              <h1 className="mt-6 font-display font-black leading-[0.95] tracking-tight text-[46px] sm:text-[64px] md:text-[80px]">
                {route.from} <span className="italic text-brand-orange font-medium">to</span> {route.to}
              </h1>
              <p className="mt-4 font-display italic text-2xl md:text-3xl text-white/90">{route.tagline}</p>
              <p className="mt-6 max-w-xl text-white/85 leading-relaxed">{route.intro}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={telLink()} className="btn-shine inline-flex items-center gap-2 rounded-full bg-white text-brand-blue px-6 py-3.5 text-sm font-semibold hover:bg-brand-orange hover:text-white transition-colors" data-testid="route-call">
                  <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
                </a>
                <a href={whatsappLink(`Hi Mamta Tours, I want a ${route.from} to ${route.to} taxi.`)} target="_blank" rel="noopener noreferrer" className="btn-shine inline-flex items-center gap-2 rounded-full bg-brand-orange text-white px-6 py-3.5 text-sm font-semibold hover:bg-white hover:text-brand-blue transition-colors" data-testid="route-whatsapp">
                  <WhatsAppIcon size={16} /> WhatsApp Now
                </a>
              </div>

              <div className="mt-10 flex items-center gap-3">
                <div className="flex text-brand-orange">
                  {[0,1,2,3,4].map((s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                </div>
                <div className="text-white/85 text-xs">
                  Trusted by <span className="font-semibold text-white">1000+</span> travellers · 4.9/5 rated
                </div>
              </div>
            </div>

            {/* Booking Card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="lg:col-span-5">
              <div className="relative rounded-[28px] bg-white/95 backdrop-blur-xl text-brand-ink p-6 md:p-7 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] border border-white/60">
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-brand-blue">Instant Booking</div>
                <div className="font-display text-2xl md:text-[26px] font-bold mt-1">
                  {route.from} → {route.to}
                </div>
                <form onSubmit={submit} className="mt-5 grid grid-cols-2 gap-3" data-testid="route-booking-form">
                  <F label="Full Name" name="name" v={form.name} on={onChange} span={2} req />
                  <F label="Mobile" name="mobile" v={form.mobile} on={onChange} type="tel" req />
                  <div>
                    <L>Vehicle</L>
                    <select name="vehicle_type" value={form.vehicle_type} onChange={onChange} className="mt-1 w-full h-11 rounded-xl border border-brand-ink/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                      {VEHICLES.map((v) => <option key={v.name}>{v.name}</option>)}
                    </select>
                  </div>
                  <F label="Pickup Date" name="pickup_date" v={form.pickup_date} on={onChange} type="date" />
                  <F label="Pickup Time" name="pickup_time" v={form.pickup_time} on={onChange} type="time" />
                  <div className="col-span-2">
                    <L>Notes</L>
                    <textarea name="message" value={form.message} onChange={onChange} rows={2} placeholder="Return trip? Sightseeing stops?" className="mt-1 w-full rounded-xl border border-brand-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-shine col-span-2 mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white h-12 font-semibold hover:bg-brand-orange transition-colors disabled:opacity-70" data-testid="route-booking-submit">
                    {loading ? "Sending..." : (<>Get Instant Quote <ChevronRight className="w-4 h-4" /></>)}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* KEY STATS */}
      <section className="bg-white py-14 md:py-20 border-b border-brand-ink/5">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat icon={RouteIcon} label="Distance" value={route.distance} />
          <Stat icon={Clock} label="Duration" value={route.duration} />
          <Stat icon={MapPin} label="From" value={route.from} />
          <Stat icon={MapPin} label="To" value={route.to} />
        </div>
      </section>

      {/* HIGHLIGHTS + ITINERARY */}
      <section className="bg-brand-cream py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">What you'll see</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-black leading-tight">{route.to} <span className="italic text-brand-orange font-medium">highlights.</span></h2>
            <ul className="mt-8 space-y-3">
              {route.highlights.map((h, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="flex items-start gap-3 text-brand-ink">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
                  <span className="text-lg">{h}</span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">A sample day</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-black leading-tight">Your <span className="italic text-brand-orange font-medium">itinerary.</span></h2>
            <ol className="mt-8 relative border-l-2 border-brand-blue/15 pl-6 space-y-6">
              {route.itinerary.map((it, i) => (
                <motion.li key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="relative">
                  <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-brand-orange ring-4 ring-brand-cream" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-blue">{it.time}</div>
                  <div className="font-display text-lg md:text-xl font-semibold mt-1">{it.step}</div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* VEHICLE OPTIONS */}
      <section className="bg-white py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Choose your ride</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-black leading-tight">Vehicle <span className="italic text-brand-orange font-medium">options.</span></h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VEHICLES.map((v, i) => (
              <a key={v.name} href={whatsappLink(`Hi, I want ${v.name} for ${route.from} to ${route.to}.`)} target="_blank" rel="noopener noreferrer" className="group rounded-2xl border border-brand-ink/8 bg-brand-cream/40 p-6 hover:bg-brand-blue hover:text-white transition-colors" data-testid={`route-vehicle-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-blue group-hover:text-brand-orange">{v.tag}</div>
                <div className="mt-2 font-display text-2xl font-bold">{v.name}</div>
                <div className="mt-1 text-sm text-brand-ink/60 group-hover:text-white/70">Seats {v.seats} · A/C · Sanitized</div>
                <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-blue group-hover:text-brand-orange">Get quote <ChevronRight className="w-4 h-4" /></div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream py-24">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Route FAQs</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-black leading-tight">{route.from} → {route.to} <span className="italic text-brand-orange font-medium">questions.</span></h2>
          <div className="mt-10 space-y-3">
            {route.faqs.map((f, i) => (
              <details key={i} className="group rounded-2xl bg-white p-6 border border-brand-ink/8" data-testid={`route-faq-${i}`}>
                <summary className="cursor-pointer font-display text-lg md:text-xl font-semibold flex items-start justify-between gap-4 list-none">
                  <span className="flex items-start gap-3"><span className="text-brand-orange text-sm font-sans font-semibold pt-1">{String(i+1).padStart(2, "0")}</span>{f.q}</span>
                  <span className="text-brand-blue text-xl group-open:rotate-45 transition-transform">＋</span>
                </summary>
                <p className="mt-3 text-brand-ink/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-14 rounded-3xl bg-brand-blue text-white p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-orange/30 blur-3xl" />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-orange">Ready to travel?</div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl font-black">Book your {route.from} → {route.to} cab in 30 seconds.</h3>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={telLink()} className="btn-shine inline-flex items-center gap-2 rounded-full bg-white text-brand-blue px-6 py-3.5 text-sm font-semibold hover:bg-brand-orange hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
                </a>
                <a href={whatsappLink(`Hi Mamta Tours, I want a ${route.from} to ${route.to} taxi.`)} target="_blank" rel="noopener noreferrer" className="btn-shine inline-flex items-center gap-2 rounded-full bg-brand-orange text-white px-6 py-3.5 text-sm font-semibold hover:bg-white hover:text-brand-blue transition-colors">
                  <WhatsAppIcon size={16} /> WhatsApp Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER ROUTES */}
      <section className="bg-white py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Explore more routes</div>
          <h3 className="mt-3 font-display text-3xl md:text-4xl font-black leading-tight">Popular <span className="italic text-brand-orange font-medium">outstation cabs.</span></h3>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {Object.values(ROUTES_DATA).filter((r) => r.slug !== route.slug).map((r) => (
              <Link key={r.slug} to={`/taxi/${r.slug}`} className="group rounded-2xl border border-brand-ink/8 p-6 hover:border-brand-blue transition-colors flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-blue/70">{r.distance} · {r.duration}</div>
                  <div className="mt-2 font-display text-2xl font-bold">{r.from} → {r.to}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-brand-blue group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl bg-brand-blue/8 text-brand-blue flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-brand-ink/50">{label}</div>
        <div className="font-display text-xl font-bold text-brand-ink">{value}</div>
      </div>
    </div>
  );
}

function L({ children }) { return <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-ink/60">{children}</label>; }
function F({ label, name, v, on, type = "text", span = 1, req }) {
  return (
    <div className={span === 2 ? "col-span-2" : "col-span-1"}>
      <L>{label}{req && <span className="text-brand-orange"> *</span>}</L>
      <input type={type} name={name} value={v} onChange={on} required={req} data-testid={`route-booking-${name}`} className="mt-1 w-full h-11 rounded-xl border border-brand-ink/15 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
    </div>
  );
}
